import firebase from "firebase/compat/app";
import "firebase/compat/auth";
import "firebase/compat/firestore";

/**
 * Production, for reading only: the source of Bestellingen → Testdata in a local or development
 * build (see functions/devTestData). Orders are not public, so this signs in with a CMS account -
 * the same login as in dev - for the length of one read, and signs out again.
 *
 * Read-only by construction: production's Firestore handles never leave this module, and the only
 * thing done with them is the query for one delivery date's orders. The config comes from
 * REACT_APP_PROD_* in .env.development; a production build has none.
 */

const CMS_ROLES = ["cmsAdmin", "cmsUser"];

const CONFIGS = {
  VCE: {
    apiKey: process.env.REACT_APP_PROD_VCE_APIKEY,
    authDomain: process.env.REACT_APP_PROD_VCE_AUTHDOMAIN,
    projectId: process.env.REACT_APP_PROD_VCE_PROJECTID,
    storageBucket: process.env.REACT_APP_PROD_VCE_STORAGEBUCKET,
    messagingSenderId: process.env.REACT_APP_PROD_VCE_MESSAGESENDERID,
    appId: process.env.REACT_APP_PROD_VCE_APPID,
  },
  HVO: {
    apiKey: process.env.REACT_APP_PROD_HVO_APIKEY,
    authDomain: process.env.REACT_APP_PROD_HVO_AUTHDOMAIN,
    projectId: process.env.REACT_APP_PROD_HVO_PROJECTID,
    storageBucket: process.env.REACT_APP_PROD_HVO_STORAGEBUCKET,
    messagingSenderId: process.env.REACT_APP_PROD_HVO_MESSAGESENDERID,
    appId: process.env.REACT_APP_PROD_HVO_APPID,
  },
};

const apps = {};

const productionApp = (shop) => {
  if (!apps[shop]) apps[shop] = firebase.initializeApp(CONFIGS[shop], `production-${shop.toLowerCase()}`);
  return apps[shop];
};

/** Production's project id for this shop, or null when this build has no production config. */
export const productionProjectId = (shop) => CONFIGS[shop]?.projectId || null;

/**
 * Sign in to the shop's production project, read the orders of one delivery date, and sign out.
 * The session is not stored anywhere: it lives in memory for the length of this call.
 *
 * @param {{ shop: "VCE"|"HVO", email: string, password: string, deliveryDate: string }} params - date as DD-MM-YYYY
 * @returns {Promise<Array<{id: string, data: object}>>} every order of that date, whatever its status
 */
export const readProductionOrders = async ({ shop, email, password, deliveryDate }) => {
  const projectId = productionProjectId(shop);
  if (!projectId) throw new Error(`${shop}: deze build heeft geen productieconfiguratie (REACT_APP_PROD_${shop}_*)`);

  const app = productionApp(shop);
  const auth = app.auth();
  await auth.setPersistence(firebase.auth.Auth.Persistence.NONE);
  let credential;
  try {
    credential = await auth.signInWithEmailAndPassword(email, password);
  } catch (error) {
    throw new Error(`${shop}: inloggen op productie (${projectId}) lukte niet (${error.code || error.message})`);
  }

  try {
    const db = app.firestore();
    const user = await db.collection("users").doc(credential.user.uid).get();
    const role = user.exists ? user.data().role : null;
    if (!CMS_ROLES.includes(role)) throw new Error(`${shop}: ${email} is geen CMS-account in ${projectId} (rol ${role || "geen"})`);

    const snapshot = await db.collection("orders").where("delivery.date", "==", deliveryDate).get();
    return snapshot.docs.map((doc) => ({ id: doc.id, data: doc.data() }));
  } finally {
    await auth.signOut();
  }
};
